import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import { toast } from 'sonner';

const ProtectedRoute = ({
  children,
  requiredPermission,
  requiredAnyPermissions = [],
  requiredAllPermissions = [],
  allowedRoles,
  requiredFeature,
}) => {
  const { isAuthenticated, user, loading, hasPermission, hasAnyPermission, hasAllPermissions, hasRole } = useAuth();
  const { isFeatureEnabled, hospitalId, activeClinicId } = useClinic();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-600 text-sm">Loading session...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Permission Checks (PBAC)
  let isAuthorized = true;

  if (requiredPermission && !hasPermission(requiredPermission)) {
    isAuthorized = false;
  }

  if (isAuthorized && requiredAnyPermissions.length > 0 && !hasAnyPermission(requiredAnyPermissions)) {
    isAuthorized = false;
  }

  if (isAuthorized && requiredAllPermissions.length > 0 && !hasAllPermissions(requiredAllPermissions)) {
    isAuthorized = false;
  }

  // Backward compatibility fallback to role check only if no permissions specified at all
  if (
    isAuthorized &&
    !requiredPermission &&
    requiredAnyPermissions.length === 0 &&
    requiredAllPermissions.length === 0 &&
    allowedRoles &&
    allowedRoles.length > 0 &&
    !hasRole(allowedRoles)
  ) {
    isAuthorized = false;
  }

  if (!isAuthorized) {
    console.warn(
      `Access denied to path "${location.pathname}". Required permission: ${requiredPermission || JSON.stringify(requiredAnyPermissions)}`
    );
    toast.error('You do not have permission to access this page.');
    const redirectUrl = hospitalId && activeClinicId
      ? `/hospital/${hospitalId}/clinic/${activeClinicId}/dashboard`
      : '/dashboard';
    return <Navigate to={redirectUrl} replace />;
  }

  if (requiredFeature && !isFeatureEnabled(requiredFeature)) {
    toast.error('This feature is disabled for this clinic');
    const redirectUrl = hospitalId && activeClinicId
      ? `/hospital/${hospitalId}/clinic/${activeClinicId}/dashboard`
      : '/dashboard';
    return <Navigate to={redirectUrl} replace />;
  }

  return children;
};

export default ProtectedRoute;

